// Fill and sign one order document.
//
// The AI writes whatever it can answer from the orders table and the chart;
// every other blank it found stays empty for whoever fills the form in to
// type. The page shows each value where it will be stamped, the side panel is
// where values are typed (a box clicked on the page jumps to its field), and
// "+ Add text box" covers a blank the scan missed. Signing is the doctor's:
// `canSign` is off for someone filling a form in for them.
//
// Coordinates are normalised 0-1000 from the top-left, end to end: the backend
// stores them, the backend stamps from them, and this file lays them out as CSS
// percentages (which is just /10). Nothing converts, so the browser and the
// stamp cannot disagree about where a value will land.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { scanOrderDoc, applyOrderDoc, getMySignature, saveMySignature } from "../../services/orderPaperwork";
import SignaturePad from "./SignaturePad";
import { formScanKey, getFormScan, setFormScan } from "../../utils/formScanCache";

const boxStyle = (box) => ({
  position: "absolute",
  left: `${box[0] / 10}%`,
  top: `${box[1] / 10}%`,
  width: `${(box[2] - box[0]) / 10}%`,
  height: `${(box[3] - box[1]) / 10}%`,
});

// A field drawn onto the page preview, showing the value that will actually be
// stamped there. Outlining the box alone was not enough — "fill out the
// paperwork" should look like filled-out paperwork before you sign it.
//
// The font size is derived from the rendered image height so the preview
// matches the stamp: the backend sizes text to the box too, and a preview at a
// different scale would misrepresent whether a value fits.
// A comfortable font as a fraction of page height: ~10.5pt on a 792pt page.
// Mirrors COMFORTABLE_FONT_PT in api/order_paperwork.py so the preview and the
// stamp agree about size, not just position.
const COMFORTABLE_FONT_FRACTION = 10.5 / 792;
const OPEN_BOX_RATIO = 2.2;

// Preview text size for a box, matching the backend's _fit_font_size: a thin
// box is a ruled LINE, whose height is the thickness of the rule rather than
// the space available, so it does not shrink the writing.
function previewFontPx(box, imageHeight) {
  // Until the page image has laid out there is nothing to measure against.
  // Falling through to 0 would render every value invisible during that frame,
  // so use a readable default and let the measured value take over.
  const height = imageHeight || 0;
  if (!height) return 13;
  const comfortable = COMFORTABLE_FONT_FRACTION * height;
  const boxPx = ((box[3] - box[1]) / 1000) * height;
  return boxPx > comfortable * OPEN_BOX_RATIO
    ? Math.min(boxPx * 0.6, comfortable * 1.6)
    : comfortable;
}

// True when a box is a genuine open writing area rather than a ruled line.
// The same test the backend applies before deciding where to put the baseline.
function isOpenArea(box, imageHeight) {
  const comfortable = COMFORTABLE_FONT_FRACTION * (imageHeight || 0);
  if (!comfortable) return false;
  return ((box[3] - box[1]) / 1000) * imageHeight > comfortable * OPEN_BOX_RATIO;
}

// Where a signature stands, relative to its box — _signature_area in
// api/order_paperwork.py, so the preview is the stamp: a ruled line (its rule
// near the box's top) gets the mark from just below the rule to half an inch
// above it; an open area holds it as it is. Page units (0-1000).
const SIGNATURE_MIN_HEIGHT = 45;
const SIGNATURE_DIP = 4;
function signatureAreaStyle(box) {
  const h = box[3] - box[1];
  if (h >= SIGNATURE_MIN_HEIGHT) return { top: "0%", height: "100%" };
  const bottom = box[1] + Math.min(SIGNATURE_DIP, h);
  const top = Math.max(bottom - SIGNATURE_MIN_HEIGHT, 0);
  return { top: `${((top - box[1]) / h) * 100}%`, height: `${((bottom - top) / h) * 100}%` };
}

// A hand-added box's id. Unique across sessions: a reopened form brings its
// saved boxes back with their ids, so a counter would collide with them.
const newBoxId = (kind) =>
  `manual-${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// A date on a signature's row is the signing date, which the server fills in
// when the form is signed — the rule (and tolerance, in 0-1000 page units) of
// _autofill_signing_dates / SIGNING_DATE_ROW_TOLERANCE in api/order_paperwork.py.
const SIGNING_DATE_ROW_TOLERANCE = 40;
function isSigningDate(field, fields) {
  if (field.kind !== "date") return false;
  const centre = (field.box[1] + field.box[3]) / 2;
  return fields.some((f) => f.kind === "signature" && f.page === field.page
    && Math.abs((f.box[1] + f.box[3]) / 2 - centre) <= SIGNING_DATE_ROW_TOLERANCE);
}

// Keep a box on the page. A field dragged off the edge cannot be stamped and
// cannot be dragged back.
function clampBox(box) {
  const w = box[2] - box[0];
  const h = box[3] - box[1];
  const x0 = Math.min(Math.max(box[0], 0), 1000 - w);
  const y0 = Math.min(Math.max(box[1], 0), 1000 - h);
  return [x0, y0, x0 + w, y0 + h];
}

// A field drawn on the page, showing the value that will be stamped there and
// letting the clinician put it where it belongs.
//
// Vision measures a ruled line well but not perfectly, so every box is
// draggable and resizable: the model proposes a position, the person
// confirms it. The value renders at the BOTTOM of the box because that is
// where the backend writes it — a preview that disagreed with the stamp was
// showing an approval for something else.
function FieldMark({ field, imageHeight, signaturePng, onChange, onSelect }) {
  const isSignature = field.kind === "signature";
  const fontPx = previewFontPx(field.box, imageHeight);
  const shown = field.kind === "checkbox" ? (field.value ? "X" : "") : field.value;

  const dragFrom = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const host = e.currentTarget.closest("[data-page-host]");
    if (!host) return;
    const rect = host.getBoundingClientRect();
    // A zero-sized rect means the page image has not laid out yet. Dragging
    // against it would divide by zero and fling the box off the sheet.
    if (!rect.width || !rect.height) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const start = [...field.box];
    // A press that never travels is a click: it goes to the field instead.
    let moved = false;
    // Only the finger (or mouse) that pressed: a second finger on a touch
    // screen neither drags the box nor ends the drag.
    const pointerId = e.pointerId;
    const mine = (ev) => pointerId == null || ev.pointerId == null || ev.pointerId === pointerId;

    const move = (ev) => {
      if (!mine(ev)) return;
      if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 3) return;
      moved = true;
      const dx = ((ev.clientX - startX) / rect.width) * 1000;
      const dy = ((ev.clientY - startY) / rect.height) * 1000;
      if (mode === "resize") {
        // Grow from the origin; never invert or collapse the box.
        onChange(field.id, [
          start[0], start[1],
          Math.min(Math.max(start[2] + dx, start[0] + 10), 1000),
          Math.min(Math.max(start[3] + dy, start[1] + 8), 1000),
        ]);
      } else {
        onChange(field.id, clampBox([
          start[0] + dx, start[1] + dy, start[2] + dx, start[3] + dy,
        ]));
      }
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
    const up = (ev) => {
      if (!mine(ev)) return;
      stop();
      if (!moved && mode === "move") onSelect?.(field.id);
    };
    // The browser took the press over (a scroll, a pinch): neither a drag nor
    // a click. Without this, the next tap anywhere finished the stale drag.
    const cancel = (ev) => {
      if (mine(ev)) stop();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  };

  return (
    <div
      aria-label={`${field.label || "Field"} field`}
      title={`${field.label}${shown ? `: ${shown}` : ""} — click to fill in, drag to move, corner to resize`}
      onPointerDown={(e) => dragFrom(e, "move")}
      style={{
        ...boxStyle(field.box),
        display: "flex",
        // Match the stamp: a thin box is a ruled line whose rule sits near the
        // TOP, so the value is written there; a tall box is an open area and
        // centres. Anchoring to the bottom struck every value through its rule.
        alignItems: isOpenArea(field.box, imageHeight) ? "center" : "flex-start",
      }}
      // touch-none: a touch on a box is the box's, so dragging it on a phone
      // moves it instead of scrolling the page (and cancelling the drag).
      className={`group cursor-move touch-none ring-1 hover:ring-2 ${
        isSignature
          ? "ring-emerald-500/70 bg-emerald-400/10 hover:ring-emerald-500"
          : shown
            ? "ring-sky-500/40 bg-sky-400/5 hover:ring-sky-500"
            // An empty blank: there to be typed into, so faint until hovered.
            : "ring-sky-400/30 hover:ring-sky-500"
      }`}
    >
      {isSignature ? (
        signaturePng ? (
          <div data-testid="signature-area" className="pointer-events-none absolute left-0 w-full"
               style={signatureAreaStyle(field.box)}>
            <img src={signaturePng} alt="Signature" className="h-full w-full object-contain object-left-bottom" />
          </div>
        ) : null
      ) : (
        <span
          className="block w-full whitespace-nowrap leading-none text-slate-900 select-none"
          style={{ fontSize: `${fontPx}px`, paddingLeft: 1 }}
        >
          {shown}
        </span>
      )}
      <span
        role="button"
        tabIndex={0}
        aria-label={`Resize ${field.label || "field"}`}
        onPointerDown={(e) => dragFrom(e, "resize")}
        className="absolute -right-1 -bottom-1 h-2.5 w-2.5 rounded-sm bg-white ring-1 ring-slate-500 opacity-0 group-hover:opacity-100 cursor-nwse-resize"
      />
    </div>
  );
}

export default function OrderPaperworkEditor({
  patientId,
  doc,
  // The items the doctor ticked. Present when this came from Generate order;
  // null when re-opening a document, where "everything" is the right scope.
  orderIds = null,
  signerName = "",
  onBack,
  onSaved,
  // A form from the Paperwork tab: saves update that form, and whoever fills
  // it in for the doctor can save it unsigned (the doctor signs it later).
  paperworkId = null,
  allowUnsigned = false,
  // Off for someone filling the form in for the doctor: they save it, the
  // doctor signs it (the server refuses their signature anyway).
  canSign = true,
  backLabel = "← Paperwork",
}) {
  const [pages, setPages] = useState([]);
  const [fields, setFields] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [savedSignature, setSavedSignature] = useState(null);
  const [padOpen, setPadOpen] = useState(false);
  const [signature, setSignature] = useState(null); // { use_saved } | { png }
  const [signaturePng, setSignaturePng] = useState(null); // what to draw in the box
  const [imageHeight, setImageHeight] = useState(0);
  const [scanning, setScanning] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  // What the SERVER told us about its own ability to stamp a PDF. Absent on an
  // older backend, which is treated as healthy rather than locking everyone out.
  const [stampBlock, setStampBlock] = useState(null);
  // Ticked orders the AI could not place on this form. Named on screen: a
  // selection that silently vanished is the worst outcome this feature has.
  const [unplaced, setUnplaced] = useState([]);
  // The side panel's inputs, by field id: a box clicked on the page (or just
  // added) focuses its input.
  const inputs = useRef({});
  const focusNext = useRef(null);

  // What the scan is FOR, as a value rather than an identity. orderIds arrives
  // as a fresh array literal on every parent render, so keying the effect on it
  // re-ran vision — two AI calls per page — whenever the modal re-rendered for
  // any reason, including clearing its selection right after a save.
  const orderKey = (orderIds || []).join(",");
  const orderIdsRef = useRef(orderIds);
  orderIdsRef.current = orderIds;

  const runScan = useCallback(async (force) => {
    setScanning(true);
    setFlash(null);
    const cacheKey = formScanKey(doc.path, orderIdsRef.current);
    // A Paperwork-tab form is always asked of the server: it answers with the
    // form's SAVED boxes, where this cache would replay its first read.
    const cacheable = !paperworkId;
    try {
      // Same form, same ticks, already read this session: use the answer we
      // have. Re-scan (force) and a different selection go to the server.
      let res = force || !cacheable ? null : getFormScan(cacheKey);
      if (res) {
        console.log("[Orders] form already read for this selection — using the cached scan");
      } else {
        res = await scanOrderDoc({
          patientId, path: doc.path, force, orderIds: orderIdsRef.current,
          ...(paperworkId ? { paperworkId } : {}),
        });
        // Only a real answer is worth keeping; an error is retried next time.
        if (cacheable && (res?.status === "ok" || res?.status === "no_fields")) setFormScan(cacheKey, res);
      }
      setPages(res.pages || []);
      setFields(res.fields || []);
      setUnplaced(Array.isArray(res.unplaced) ? res.unplaced : []);
      setStampBlock(res.can_stamp === false ? (res.stamp_reason || "unknown reason") : null);
      if (res.status === "no_fields") {
        setFlash({
          type: "info",
          text: canSign
            ? "The AI could not fill anything in on this document. You can still sign it, or add a text box to write on it."
            : "The AI could not fill anything in on this document — add a text box to write on it.",
        });
      }
    } catch (err) {
      setFlash({ type: "error", text: err?.message || String(err) });
    } finally {
      setScanning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, doc.path, orderKey, paperworkId, canSign]);

  // An EMPTY order set is never a reason to re-read a form already read: it
  // means the parent dropped its selection, not that a different requisition
  // is wanted. Without this the save handler's tidy-up cost a second scan.
  const scannedOnce = useRef(false);
  useEffect(() => {
    if (scannedOnce.current && !orderKey) return;
    scannedOnce.current = true;
    runScan(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runScan]);

  useEffect(() => {
    let cancelled = false;
    getMySignature()
      .then((r) => { if (!cancelled) setSavedSignature(r?.signature || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Detection can legitimately find nothing on a dense photocopy, and signing
  // is the reason this screen exists — so the box can always be placed by hand.
  // Vision measures a rule well but not perfectly, so the position is the
  // clinician's to correct. What they leave on screen is what gets stamped.
  const setBox = useCallback((id, box) =>
    setFields((fs) => fs.map((f) => (f.id === id ? { ...f, box } : f))), []);

  const addSignatureBox = useCallback(() => {
    setFields((fs) => [...fs, {
      id: newBoxId("sig"), page: pageNo, label: "Signature", kind: "signature",
      box: [150, 880, 550, 920], value: "", source: null,
    }]);
  }, [pageNo]);

  // Typing over the AI's answer makes it the typist's: no source is claimed.
  const setValue = useCallback((id, value) =>
    setFields((fs) => fs.map((f) => (f.id === id ? { ...f, value, source: null } : f))), []);

  const toggleCheck = useCallback((id) =>
    setFields((fs) => fs.map((f) => (f.id === id ? { ...f, value: f.value ? "" : "X", source: null } : f))), []);

  const focusField = useCallback((id) => {
    const el = inputs.current[id];
    if (!el) return;
    el.focus();
    el.scrollIntoView?.({ block: "nearest" });
  }, []);

  // A box clicked on the page: a checkbox ticks; anything else goes to its field.
  const selectField = useCallback((id) => {
    const field = fields.find((f) => f.id === id);
    if (field?.kind === "checkbox") toggleCheck(id);
    else focusField(id);
  }, [fields, toggleCheck, focusField]);

  // For a blank the scan did not find: a box to drag into place and type in.
  const addTextBox = useCallback(() => {
    const id = newBoxId("text");
    focusNext.current = id;
    setFields((fs) => [...fs, {
      id, page: pageNo, label: "Text", kind: "text",
      box: [150, 480, 550, 510], value: "", source: null,
    }]);
  }, [pageNo]);

  useEffect(() => {
    if (focusNext.current && inputs.current[focusNext.current]) {
      focusField(focusNext.current);
      focusNext.current = null;
    }
  }, [fields, focusField]);

  // A value someone typed cites no source (the AI's always do), and a re-scan
  // replaces every field — so it asks first.
  const rescan = () => {
    const typed = fields.some((f) => f.kind !== "signature" && f.value && !f.source);
    if (typed && !window.confirm(
      "Re-scan reads the form again and replaces what is filled in, including what you typed. Re-scan?",
    )) return;
    runScan(true);
  };

  const pageFields = useMemo(
    () => fields.filter((f) => f.page === pageNo),
    [fields, pageNo]
  );
  const signatureFields = useMemo(
    () => fields.filter((f) => f.kind === "signature"),
    [fields]
  );
  const page = pages.find((p) => p.page === pageNo) || pages[0];
  const signerLabel =
    signature?.signer_name || savedSignature?.signer_name || signerName || "";

  const handleSignature = async ({ png, signerName: typedName, remember, useSaved }) => {
    if (useSaved) {
      setSignature({ use_saved: true });
      setSignaturePng(savedSignature?.signature_png || null);
      setPadOpen(false);
      return;
    }
    setSignaturePng(png);
    // Sign with the PNG in hand, always. Storing it is a separate favour for
    // NEXT time. The old code saved it, threw it away, and sent
    // `{use_saved:true}` so the server would fetch back what the browser was
    // already holding — and when that lookup missed, it told a clinician who
    // had just drawn a signature to "draw one first".
    setSignature({ png, signer_name: typedName || signerName });
    if (remember) {
      try {
        await saveMySignature({ png, signerName: typedName || signerName });
        setSavedSignature({ signature_png: png, signer_name: typedName });
      } catch (err) {
        setFlash({
          type: "info",
          text: `Signature not saved for reuse (${err.message}), but it is on this document.`,
        });
      }
    }
    setPadOpen(false);
  };

  // Signing with a signature already on file should not ask for it again —
  // it is the same mark every time, which is why it was saved. The pad is
  // still one click away behind the pencil, for changing it.
  const applySignature = useCallback(() => {
    // A form the AI found no signature line on still has to be signed, so give
    // the signature somewhere to go before capturing it.
    if (signatureFields.length === 0) addSignatureBox();
    if (savedSignature?.signature_png) {
      setSignature({ use_saved: true });
      setSignaturePng(savedSignature.signature_png);
      return;
    }
    setPadOpen(true);
  }, [savedSignature, signatureFields.length, addSignatureBox]);

  const save = async ({ unsigned = false } = {}) => {
    if (stampBlock) {
      setFlash({
        type: "error",
        text: "This server cannot fill in documents yet — nothing was saved.",
      });
      return;
    }
    if (!signature && !unsigned) {
      setFlash({ type: "error", text: "Sign the order first." });
      return;
    }
    setSaving(true);
    setFlash(null);
    try {
      // From component state, which is what the screen showed you.
      const res = await applyOrderDoc({
        patientId, path: doc.path, fields,
        signature: unsigned ? undefined : signature || undefined,
        paperworkId: paperworkId || undefined,
      });
      setFlash({
        type: "success",
        text: `Saved ${res.signed_path.split("/").pop()}${res.signed ? " — signed" : ""}.`,
      });
      onSaved?.(res);
    } catch (err) {
      setFlash({ type: "error", text: err?.message || String(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={onBack}
          className="text-xs px-2 py-1 rounded ring-1 ring-slate-300 dark:ring-slate-600"
        >
          {backLabel}
        </button>
        <div className="text-sm font-semibold truncate">{doc.filename}</div>

        <button
          type="button"
          onClick={rescan}
          disabled={scanning || saving}
          className="ml-auto shrink-0 text-xs px-2 py-1 rounded ring-1 ring-slate-300 dark:ring-slate-600 disabled:opacity-50"
          title="Run detection again. Costs an AI call per page."
        >
          ↻ Re-scan
        </button>

        {/* Sign and Save live up here, on every page. They used to sit in a
            footer below a scrolling page image, and Sign existed only beside
            its own field — so page 2 of a 2-page form offered no way to sign
            the line on page 1. */}
        {!scanning && !padOpen && (
          <>
            <button
              type="button"
              onClick={addTextBox}
              title="A box to type in, for a blank the scan did not find"
              className="shrink-0 text-xs px-2 py-1 rounded ring-1 ring-slate-300 dark:ring-slate-600"
            >
              + Add text box
            </button>
            {canSign && signatureFields.length === 0 && (
              <button
                type="button"
                onClick={addSignatureBox}
                className="shrink-0 text-xs px-2 py-1 rounded ring-1 ring-slate-300 dark:ring-slate-600"
              >
                + Add signature box
              </button>
            )}
            {!canSign ? null : signature ? (
              <span
                data-testid="signed-badge"
                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300 dark:ring-emerald-500/40"
                title="Not saved yet — press Apply & Save to write it to the form"
              >
                ✓ Signed
                {signerLabel && (
                  <span className="font-normal opacity-80">· {signerLabel}</span>
                )}
              </span>
            ) : (
              <button
                type="button"
                onClick={applySignature}
                className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm"
              >
                ✍️ Sign
              </button>
            )}
            {/* Only offered once there is something to change. */}
            {canSign && savedSignature && (
              <button
                type="button"
                aria-label="Edit signature"
                title="Draw a new signature"
                onClick={() => setPadOpen(true)}
                className="shrink-0 px-1.5 py-1 rounded ring-1 ring-slate-300 dark:ring-slate-600 text-xs"
              >
                ✏️
              </button>
            )}
            {(allowUnsigned || !canSign) && (
              <button
                type="button"
                onClick={() => save({ unsigned: true })}
                disabled={saving || !!stampBlock}
                title="Save what is filled in; the doctor signs it later"
                className={canSign
                  ? "shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600 disabled:opacity-50"
                  : "shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm disabled:opacity-50"}
              >
                {canSign ? "Save without signing" : saving ? "Saving…" : "Save"}
              </button>
            )}
            {canSign && (
            <button
              type="button"
              onClick={() => save()}
              disabled={saving || !signature || !!stampBlock}
              title={
                stampBlock
                  ? "This server cannot fill in documents yet"
                  : signature ? "" : "Sign the order before saving it"
              }
              className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Apply & Save"}
            </button>
            )}
          </>
        )}
      </div>

      {stampBlock && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/[0.08] px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          <div className="font-semibold">
            This server cannot fill in documents yet — don&apos;t fill this one in.
          </div>
          <div className="mt-0.5 text-[12px]">
            You can read the form and sign, but Apply &amp; Save is turned off
            because the finished PDF cannot be produced. The backend is missing
            a package: <span className="font-mono">{stampBlock}</span>. Run{" "}
            <span className="font-mono">pip install -r requirements.txt</span> in
            the backend environment and restart it.
          </div>
        </div>
      )}

      {unplaced.length > 0 && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/[0.08] px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          <div className="font-semibold">
            {unplaced.length === 1 ? "One selected order was" : `${unplaced.length} selected orders were`} not placed on this form
          </div>
          {/* Scrolls: a long list must never squeeze the page and its fields away. */}
          <ul className="mt-1 list-disc pl-5 text-[12px] max-h-24 overflow-auto">
            {unplaced.map((o) => (
              <li key={o.key}>{o.description || o.key}</li>
            ))}
          </ul>
          <div className="mt-1 text-[12px]">
            The AI found no box or write-in line for {unplaced.length === 1 ? "it" : "them"}.
            Add a text box and type {unplaced.length === 1 ? "it" : "them"} in, or pick a different form.
          </div>
        </div>
      )}

      {flash && (
        <div
          role="status"
          className={
            flash.type === "error"
              ? "mb-3 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm font-semibold text-rose-800 dark:text-rose-200"
              : flash.type === "success"
                ? "mb-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/[0.08] px-3 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200"
                : "mb-3 rounded-md border border-sky-300 bg-sky-50 dark:bg-sky-500/[0.08] px-3 py-2 text-sm font-semibold text-sky-800 dark:text-sky-200"
          }
        >
          {flash.text}
        </div>
      )}

      {scanning && (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-500 dark:text-slate-400" role="status" aria-live="polite">
          <div className="h-10 w-10 rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-amber-500 animate-spin" />
          <div className="text-sm font-semibold">Reading the form…</div>
          <div className="text-xs">Finding the blanks and the signature lines. This takes a moment per page.</div>
        </div>
      )}

      {!scanning && padOpen && (
        <SignaturePad
          initialPng={savedSignature?.signature_png || null}
          signerName={savedSignature?.signer_name || signerName}
          onSave={handleSignature}
          onCancel={() => setPadOpen(false)}
        />
      )}

      {!scanning && !padOpen && (
        <div className="flex-1 min-h-0 grid grid-cols-[1fr_340px] gap-4">
          <div className="min-h-0 overflow-auto">
            {page ? (
              <div className="relative inline-block w-full" data-page-host>
                <img
                  src={page.image_data_url}
                  alt={`${doc.filename} page ${page.page}`}
                  className="w-full block select-none"
                  draggable={false}
                  onLoad={(e) => setImageHeight(e.currentTarget.clientHeight)}
                />
                {pageFields.map((f) => (
                  <FieldMark
                    key={f.id}
                    field={f}
                    imageHeight={imageHeight}
                    signaturePng={signaturePng}
                    onChange={setBox}
                    onSelect={selectField}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">Nothing to show for this document.</div>
            )}

            {pages.length > 1 && (
              <div className="mt-3 flex items-center gap-1">
                {pages.map((p) => (
                  <button
                    key={p.page}
                    type="button"
                    onClick={() => setPageNo(p.page)}
                    className={`text-xs px-2 py-1 rounded ring-1 ${
                      p.page === pageNo
                        ? "bg-slate-700 text-white ring-slate-700"
                        : "ring-slate-300 dark:ring-slate-600"
                    }`}
                  >
                    Page {p.page}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="min-h-0 overflow-auto border-l border-slate-200 dark:border-slate-700 pl-3">
            <div className="text-xs font-semibold mb-2">
              Fields on page {pageNo} ({pageFields.length})
            </div>
            {pageFields.length === 0 && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                No blanks found on this page — add a text box to write on it.
              </div>
            )}
            <ul className="space-y-2">
              {pageFields.map((f) => (
                <li key={f.id} className="rounded-md p-2 ring-1 ring-slate-200 dark:ring-slate-700">
                  <div className="flex items-center gap-1 text-[11px] font-semibold">
                    <label htmlFor={`paperwork-field-${f.id}`} className="truncate">{f.label || "Field"}</label>
                    {f.source && (
                      <span
                        className="ml-auto shrink-0 rounded bg-slate-100 dark:bg-slate-800 px-1 text-[10px] font-mono text-slate-500 dark:text-slate-400"
                        title="Where this value came from"
                      >
                        {f.source}
                      </span>
                    )}
                  </div>
                  {f.kind === "signature" ? (
                    <div
                      className={`mt-1 text-xs font-semibold ${
                        signature
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-slate-400"
                      }`}
                    >
                      {!canSign ? "The doctor signs here" : signature ? "✍️ Signed" : "Not signed yet — use Sign above"}
                    </div>
                  ) : f.kind === "checkbox" ? (
                    <input
                      id={`paperwork-field-${f.id}`}
                      ref={(el) => { inputs.current[f.id] = el; }}
                      type="checkbox"
                      checked={Boolean(f.value)}
                      onChange={() => toggleCheck(f.id)}
                      className="mt-1 h-4 w-4 accent-sky-600"
                    />
                  ) : (
                    <input
                      id={`paperwork-field-${f.id}`}
                      ref={(el) => { inputs.current[f.id] = el; }}
                      type="text"
                      value={f.value || ""}
                      onChange={(e) => setValue(f.id, e.target.value)}
                      placeholder={isSigningDate(f, fields) ? "today's date, when signed" : undefined}
                      className="mt-1 w-full rounded px-1.5 py-1 text-xs ring-1 ring-slate-300 dark:ring-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!scanning && !padOpen && (
        <div className="mt-3 border-t border-slate-200 dark:border-slate-700 pt-2 text-[11px] text-slate-500 dark:text-slate-400">
          The AI fills what it can; type into any field, or add a box. Empty boxes stay blank. The original file is never changed.
        </div>
      )}
    </div>
  );
}
